import React, { useCallback, useEffect, useState, useContext } from 'react';
import { Box, Grid, TextField, InputLabel } from '@mui/material';
import formatString from 'format-string-by-pattern';
import { FormikProps, useFormikContext } from 'formik';
import { CommonDataContext } from '../../context';
import { FieldType } from '../types';
import _ from 'lodash';
import { FieldLabelWithFallback } from './FieldLabelWithFallback';

export const PhoneField = ({
  itemData,
  value,
  locale,
  isNeedToMoveLabelOutOfInput,
}: {
  itemData: FieldType;
  value: any;
  locale: string;
  displayLabel: string;
  isNeedToMoveLabelOutOfInput: boolean;
}) => {
  const { id: fieldId, config: fieldConfig } = itemData;
  const [isInvalid, setIsInvalid] = useState(false);
  const formik: FormikProps<any> = useFormikContext();
  const { isMobileView, highlightErrorFieldsState } = useContext(CommonDataContext);

  const displayLabel = (
    <FieldLabelWithFallback
      fieldLabel={itemData.config.fieldLabel}
      displayLabel={itemData.config.displayLabel}
    />
  );

  const evaluateIfInvalid = (val: string): boolean => {
    const isRequiredAndEmpty = !!fieldConfig.required && _.isEmpty(val);
    return isRequiredAndEmpty || _.size(val.replace(/[^\d]/g, '')) !== 10;
  };

  const handleChange = useCallback((e) => {
    e.target.value = e.target.value.replace(/[^0-9]/g, '');
    const formattedValue = formatString('(999) 999-9999', e.target.value);
    e.target.value = formattedValue;
    formik.setFieldValue(itemData.config.fieldName, e.target.value, false);
    setIsInvalid(evaluateIfInvalid(e.target.value));
  }, []);

  const _resetField = (): void => {
    // Check if error is already set to ''
    if (Boolean(_.get(formik.errors, itemData.config.fieldName))) {
      //Remove Error
      formik.setFieldError(itemData.config.fieldName, '');
    }

    if (!_.has(formik.values, itemData.config.fieldName)) {
      // do not set '' value for untouched fields
      return;
    }

    //Erase value
    formik.setFieldValue(itemData.config.fieldName, '');
  };

  useEffect(() => {
    if (value) {
      setIsInvalid(evaluateIfInvalid(value));
    }
    return () => {
      formik.setFieldError(itemData.config.fieldName, '');
    };
  }, []);

  useEffect(() => {
    //When there is no need to display the field, we reset it's state to a clean one
    if (!itemData.config.displayed) {
      _resetField();
    } else {
      let isEmpty = false;
      if (itemData.config.required) {
        isEmpty = _.isEmpty(value);
      }
      formik.setFieldError(itemData.config.fieldName, isInvalid || isEmpty ? 'required' : '');
    }
  }, [itemData.config.displayed, itemData.config.required, isInvalid, value]);

  useEffect(() => {
    if (highlightErrorFieldsState.state) {
      setIsInvalid(!!formik.getFieldMeta(itemData.config.fieldName).error);
    }
  }, [highlightErrorFieldsState]);

  return (
    <Box
      component={Grid}
      item
      xl={_.get(fieldConfig, 'columns.xl', 12)}
      lg={_.get(fieldConfig, 'columns.lg', 12)}
      md={_.get(fieldConfig, 'columns.md', 12)}
      sm={_.get(fieldConfig, 'columns.sm', 12)}
      xs={_.get(fieldConfig, 'columns.xs', 12)}
      display={{
        xl: !itemData.config.displayed ? 'none' : 'block',
        lg: !itemData.config.displayed ? 'none' : 'block',
        md: !itemData.config.displayed ? 'none' : 'block',
        sm: !itemData.config.displayed ? 'none' : 'block',
        xs: !itemData.config.displayed ? 'none' : 'block',
      }}
    >
      {isNeedToMoveLabelOutOfInput && (
        <InputLabel className="outer-label">{displayLabel}</InputLabel>
      )}
      <TextField
        key={fieldId}
        disabled={fieldConfig.disabled}
        name={fieldConfig.fieldName}
        fullWidth={fieldConfig.fullWidth}
        variant={isMobileView ? 'outlined' : 'standard'}
        label={isNeedToMoveLabelOutOfInput ? '' : displayLabel}
        error={isInvalid}
        required={fieldConfig.required}
        helperText={isInvalid ? _.get(fieldConfig, `errorMessage.${locale}`, '') : ''}
        value={value ? value : ''}
        onChange={handleChange}
      />
    </Box>
  );
};
